/*!

=========================================================
* Black Dashboard PRO React - v1.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/black-dashboard-pro-react
* Copyright 2020 Creative Tim (https://www.creative-tim.com)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React, { useEffect, useMemo } from "react";
import { Route, Switch, Redirect, useLocation } from "react-router-dom";
import apiService from "services/api";
//
// javascript plugin used to create scrollbars on windows
// import PerfectScrollbar from "perfect-scrollbar";

// core components
import AdminNavbar from "components/Navbars/AdminNavbar.js";
import Footer from "components/Footer/Footer.js";
import Sidebar from "components/Sidebar/Sidebar.js";

import { routes } from "routes.js";

import logo from "assets/img/genius-logo192.png";

import { LoginPage } from "pages";

import { permissionCheck } from "utils/permissionCheck";
import { ImageModal, ProtectedImg } from "components";
import { imageModalReducerActions, useImageModalContext } from "contexts/imageModal";

const processRoutes = (routes, token) =>
	routes.filter((prop, key) => {
		if (prop.collapse) {
			return (prop.views = processRoutes(prop.views, token)).length;
		}
		if (prop.role) {
			return permissionCheck(token, prop.role.model, prop.role.permissions, prop.role.check || undefined);
		}
		return false;
	});

const getRoutes = (routes) => {
	return routes.map((prop, key) => {
		if (prop.collapse) {
			return getRoutes(prop.views);
		}
		return <Route path={prop.layout + prop.path} component={prop.component} key={key} exact={prop.exact} />;
	});
};

const getActiveRoute = (routes) => {
	let activeRoute = "Genius Staff";
	for (let i = 0; i < routes.length; i++) {
		if (routes[i].collapse) {
			let collapseActiveRoute = getActiveRoute(routes[i].views);
			if (collapseActiveRoute !== activeRoute) {
				return collapseActiveRoute;
			}
		} else {
			if (window.location.pathname.indexOf(routes[i].layout + routes[i].path) !== -1) {
				return routes[i].name;
			}
		}
	}
	return activeRoute;
};

const App = (props) => {
	const { state: imageModalState, dispatch: imageModalDispatch } = useImageModalContext();
	const [activeColor, setActiveColor] = React.useState("blue");
	const [sidebarMini, setSidebarMini] = React.useState(true);
	const [opacity, setOpacity] = React.useState(0);
	const [sidebarOpened, setSidebarOpened] = React.useState(false);
	const mainPanelRef = React.useRef(null);

	const location = useLocation();
	useEffect(() => {
		document.title = "Genius Staff Dashboard";
		document.documentElement.scrollTop = 0;
		document.scrollingElement.scrollTop = 0;
		if (mainPanelRef.current) {
			mainPanelRef.current.scrollTop = 0;
		}
	}, [location]);

	// removed so that it works on windows
	// useEffect(() => {
	//     let innerMainPanelRef = mainPanelRef;
	//     // if (navigator.platform.indexOf("Win") > -1) {
	//     document.documentElement.classList.add("perfect-scrollbar-on");
	//     document.documentElement.classList.remove("perfect-scrollbar-off");
	//     ps = new PerfectScrollbar(mainPanelRef.current);
	//     mainPanelRef.current &&
	//         mainPanelRef.current.addEventListener(
	//             "ps-scroll-y",
	//             showNavbarButton
	//         );
	//     let tables = document.querySelectorAll(".table-responsive");
	//     for (let i = 0; i < tables.length; i++) {
	//         ps = new PerfectScrollbar(tables[i]);
	//     }
	//     // }
	//     window.addEventListener("scroll", showNavbarButton);
	//     return function cleanup() {
	//         if (navigator.platform.indexOf("Win") > -1) {
	//             ps.destroy();
	//             document.documentElement.classList.add("perfect-scrollbar-off");
	//             document.documentElement.classList.remove(
	//                 "perfect-scrollbar-on"
	//             );
	//             innerMainPanelRef.current &&
	//                 innerMainPanelRef.current.removeEventListener(
	//                     "ps-scroll-y",
	//                     showNavbarButton
	//                 );
	//         }
	//         window.removeEventListener("scroll", showNavbarButton);
	//     };
	// }, []);
	// const showNavbarButton = () => {
	//     if (
	//         document.documentElement.scrollTop > 50 ||
	//         document.scrollingElement.scrollTop > 50 ||
	//         (mainPanelRef.current && mainPanelRef.current.scrollTop > 50)
	//     ) {
	//         setOpacity(1);
	//     } else if (
	//         document.documentElement.scrollTop <= 50 ||
	//         document.scrollingElement.scrollTop <= 50 ||
	//         (mainPanelRef.current && mainPanelRef.current.scrollTop <= 50)
	//     ) {
	//         setOpacity(0);
	//     }
	// };

	const processedRoutes = processRoutes(routes, apiService.auth.token.get());

	const handleMiniClick = () => {
		let notifyMessage = "Sidebar mini ";
		if (document.body.classList.contains("sidebar-mini")) {
			setSidebarMini(false);
			// notifyMessage += "deactivated...";
		} else {
			setSidebarMini(true);
			// notifyMessage += "activated...";
		}
		let options = {
			place: "tr",
			message: notifyMessage,
			type: "primary",
			icon: "tim-icons icon-bell-55",
			autoDismiss: 7,
		};
		// notificationAlertRef.current.notificationAlert(options);
		document.body.classList.toggle("sidebar-mini");
	};
	const toggleSidebar = () => {
		setSidebarOpened(!sidebarOpened);
		document.documentElement.classList.toggle("nav-open");
	};
	const closeSidebar = () => {
		setSidebarOpened(false);
		document.documentElement.classList.remove("nav-open");
	};

	useEffect(() => {
		const checkAuth = () => apiService.auth.isLoggedIn() && apiService.auth.refresh();
		checkAuth();
		window.addEventListener("focus", checkAuth);
		return () => window.removeEventListener("focus", checkAuth);
	}, []);

	if (!apiService.auth.isLoggedIn()) return <LoginPage />;

	return (
		<>
			<ImageModal
				open={imageModalState.open}
				hideModal={() =>
					imageModalDispatch({
						type: imageModalReducerActions.HIDE,
					})
				}
			>
				<ProtectedImg src={imageModalState.src} />
			</ImageModal>
			<div className="wrapper">
				<div className="navbar-minimize-fixed" style={{ opacity: opacity }}>
					<button className="minimize-sidebar btn btn-link btn-just-icon" onClick={handleMiniClick}>
						<i className="tim-icons icon-align-center visible-on-sidebar-regular text-muted" />
						<i className="tim-icons icon-bullet-list-67 visible-on-sidebar-mini text-muted" />
					</button>
				</div>
				<Sidebar
					{...props}
					routes={processedRoutes}
					activeColor={activeColor}
					logo={{
						outterLink: "",
						text: "Genius STAFF",
						imgSrc: logo,
					}}
					closeSidebar={closeSidebar}
				/>
				<div className="main-panel" ref={mainPanelRef} data={activeColor}>
					<AdminNavbar
						{...props}
						handleMiniClick={handleMiniClick}
						brandText={getActiveRoute(processedRoutes)}
						sidebarOpened={sidebarOpened}
						toggleSidebar={toggleSidebar}
					/>
					<Switch>
						{getRoutes(processedRoutes)}
						<Redirect from="*" to="/" />
					</Switch>
					{
						// we don't want the Footer to be rendered on full screen maps page
						props.location.pathname.indexOf("full-screen-map") === -1 ? null : <Footer fluid />
					}
				</div>
				{/* <FixedPlugin
        activeColor={activeColor}
        sidebarMini={sidebarMini}
        handleActiveClick={handleActiveClick}
        handleMiniClick={handleMiniClick}
      /> */}
			</div>
		</>
	);
};

export default App;
